import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { tokenStore } from '@/api/tokenStore';

import { sessionSchema } from './auth';

/** Schemas mirroring the social half of `app/schemas/auth.py`. */

export const authProvidersSchema = z.object({
  google: z.object({ enabled: z.boolean(), client_id: z.string().nullable().optional() }),
  whatsapp: z.object({ enabled: z.boolean() }),
  /** Optional so an older backend, which does not send it, reads as off. */
  captcha: z
    .object({
      enabled: z.boolean(),
      site_key: z.string().nullable().optional(),
      whatsapp_code_always: z.boolean().optional().default(false),
    })
    .optional(),
});

export const socialSessionSchema = sessionSchema.extend({
  is_new_account: z.boolean().optional().default(false),
});

export const whatsAppCodeSchema = z.object({
  message: z.string(),
  expires_in: z.number(),
  resend_after: z.number(),
});

export const whatsAppVerifySchema = z.object({
  session: socialSessionSchema.nullable().optional(),
  signup_token: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
});

export type AuthProviders = z.infer<typeof authProvidersSchema>;
export type SocialSession = z.infer<typeof socialSessionSchema>;
export type WhatsAppCodeSent = z.infer<typeof whatsAppCodeSchema>;

/** A partner's code, carried by the routes that can create an account. */
export interface ReferralPayload {
  referral_code?: string;
  referral_source?: 'FORM' | 'LINK';
}

export function fetchAuthProviders(): Promise<AuthProviders> {
  return apiRequest('/auth/providers', authProvidersSchema, { skipAuthRefresh: true });
}

function adopt(session: SocialSession): SocialSession {
  tokenStore.set(session.access_token);
  return session;
}

/** Redeem the one-time code Google's popup handed back. */
export async function signInWithGoogle(
  code: string,
  referral: ReferralPayload = {},
): Promise<SocialSession> {
  const session = await apiRequest('/auth/google', socialSessionSchema, {
    method: 'POST',
    body: { code, ...referral },
    skipAuthRefresh: true,
  });
  return adopt(session);
}

export function requestWhatsAppCode(
  phone: string,
  captchaToken?: string,
): Promise<WhatsAppCodeSent> {
  return apiRequest('/auth/whatsapp/request-code', whatsAppCodeSchema, {
    method: 'POST',
    body: { phone, captcha_token: captchaToken },
    skipAuthRefresh: true,
  });
}

export type WhatsAppVerification =
  | { kind: 'signed-in'; session: SocialSession }
  | { kind: 'needs-profile'; signupToken: string; phone: string };

/**
 * Check a code. A number that already has an account is signed straight in;
 * a new one comes back with a ticket to finish signing up.
 */
export async function verifyWhatsAppCode(
  phone: string,
  code: string,
): Promise<WhatsAppVerification> {
  const result = await apiRequest('/auth/whatsapp/verify', whatsAppVerifySchema, {
    method: 'POST',
    body: { phone, code },
    skipAuthRefresh: true,
  });
  if (result.session) return { kind: 'signed-in', session: adopt(result.session) };
  return {
    kind: 'needs-profile',
    signupToken: result.signup_token ?? '',
    phone: result.phone ?? phone,
  };
}

export async function completeWhatsAppSignup(
  payload: { signup_token: string; full_name?: string; email?: string } & ReferralPayload,
): Promise<SocialSession> {
  const session = await apiRequest('/auth/whatsapp/complete', socialSessionSchema, {
    method: 'POST',
    body: payload,
    skipAuthRefresh: true,
  });
  return adopt(session);
}
